import { cloneElement, isValidElement, ReactNode, Children } from 'react';
import { PlusIcon } from '@heroicons/react/20/solid';
import { TooltipV2 } from '../tooltip/TooltipV2';

type Size = 'mini' | 'xs' | 'sm' | 'base' | 'lg' | 'xl' | 'jumbo';

const styles = {
    sizes: {
        mini: '-space-x-1',
        xs: '-space-x-1',
        sm: '-space-x-2',
        base: '-space-x-2',
        lg: '-space-x-3',
        xl: '-space-x-3',
        jumbo: '-space-x-4',
    },
    texts: {
        mini: 'text-[10px] leading-[14px]',
        xs: 'text-xs',
        sm: 'text-sm',
        base: 'text-base',
        lg: 'text-lg',
        xl: 'text-xl',
        jumbo: 'text-4xl',
    },
    buttonSizes: {
        mini: 'h-5 w-5',
        xs: 'h-6 w-6',
        sm: 'h-8 w-8',
        base: 'h-10 w-10',
        lg: 'h-12 w-12',
        xl: 'h-14 w-14',
        jumbo: 'h-[100px] w-[100px]',
    },
    iconSizes: {
        mini: 'w-3.5 h-3.5',
        xs: 'h-4 w-4',
        sm: 'h-5 w-5',
        base: 'h-6 w-6',
        lg: 'h-7 w-7',
        xl: 'h-7 w-7',
        jumbo: 'h-[50px] w-[50px]',
    },
};

export type AvatarGroupV2Props = {
    /** Size for every avatar in the group (Will overide size prop on Avatar component) */
    size: Size;
    /** Whether every avatar in the group should be rounded (Will overide rounded prop on Avatar component) */
    rounded?: boolean;
    children: ReactNode[];
    onAdd?: () => void;
};

/**
 * Renders Avatar components in a group
 * @param props
 */
function AvatarGroupV2({ children, size, rounded = true, onAdd = undefined }: AvatarGroupV2Props): JSX.Element {
    const avatars: ReactNode[] = [];
    const extraAvatars: { content: string; icon?: ReactNode }[] = [];

    Children.forEach(children, (child, idx) => {
        if (isValidElement(child)) {
            if (idx < 3) {
                const clone = cloneElement(child, {
                    ring: true,
                    rounded,
                    size,
                    ...child.props,
                });
                avatars.push(clone);
            } else {
                extraAvatars.push({ content: child.props.tooltip, icon: child.props.badge });
            }
        }
    });

    return (
        <div className="flex items-center grow-0">
            <div className={`isolate flex ${styles.sizes[size]} items-center`}>
                {/* Copy the avatar components, giving them the outer ring style */}
                {avatars.map((avatar, idx) => {
                    return (
                        <div key={idx} className="relative" style={{ zIndex: 3 - idx }}>
                            {avatar}
                        </div>
                    );
                })}
            </div>

            {onAdd && (
                <div className="">
                    <button
                        onClick={onAdd}
                        className={`${
                            rounded ? 'rounded-full' : 'rounded-md'
                        } flex items-center justify-center bg-gray-100 dark:bg-white/5 ${styles.buttonSizes[size]}`}
                    >
                        <PlusIcon className={`text-gray-600 dark:text-gray-200 ${styles.iconSizes[size]}`} />
                    </button>
                </div>
            )}
            {extraAvatars.length > 0 && (
                <div className="w-fit">
                    <TooltipV2 message={extraAvatars} position="right">
                        <label className={`ml-1 text-gray-600 dark:text-gray-200 font-medium ${styles.texts[size]}`}>
                            +{extraAvatars.length}
                        </label>
                    </TooltipV2>
                </div>
            )}
        </div>
    );
}

export default AvatarGroupV2;
